import { useState } from 'react'
import { Typography, type TypographyVariant } from '@components/atoms/Typography'
import type { User } from '../../../types/user'
import styles from './Avatar.module.scss'

export type AvatarSize = 'sm' | 'md' | 'lg'

/**
 * The minimal user shape Avatar needs. Lifted to a Pick<User, …> so any
 * caller holding the canonical `User` (or a subset of it) can pass it
 * directly without re-declaring the prop.
 */
export type AvatarUser = Pick<User, 'name' | 'avatarUrl' | 'initials'>

export interface AvatarProps {
  user: AvatarUser
  /** 24 / 32 / 40 px per the Catalyst Mobile spec. Default `'md'`. */
  size?: AvatarSize
  /** When supplied, the avatar is rendered as a `<button>` and tappable. */
  onClick?: (user: AvatarUser) => void
  /** Adds the brand focus ring as a persistent 2 px outline. */
  showRing?: boolean
  className?: string
}

// --------------------------------------------------------------------------
// Initials derivation
// --------------------------------------------------------------------------
function deriveInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return ''
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  const first = parts[0].charAt(0)
  const last = parts[parts.length - 1].charAt(0)
  return `${first}${last}`.toUpperCase()
}

// --------------------------------------------------------------------------
// Color palette rotation
// --------------------------------------------------------------------------
// 7-colour rotation that matches the Catalyst Mobile Avatar swatch
// (blue / pink / yellow / purple / green / red / orange). Algorithm port
// from oro-ui-kit's `getAvatarColorPalette`: hash the first two chars of
// the initials so the same user always lands on the same colour.
const PALETTE = [
  'blue',
  'pink',
  'yellow',
  'purple',
  'green',
  'red',
  'orange',
] as const
type PaletteColor = (typeof PALETTE)[number]
const ASCII_A = 97

function pickColor(seed: string): PaletteColor {
  if (!seed) return PALETTE[0]
  const lower = seed.toLowerCase()
  const a = (lower.charCodeAt(0) || ASCII_A) - ASCII_A
  const b = (lower.charCodeAt(1) || ASCII_A) - ASCII_A
  return PALETTE[((a + b) % PALETTE.length + PALETTE.length) % PALETTE.length]
}

// --------------------------------------------------------------------------
// Typography per size — matches the Figma Catalyst Mobile component
// --------------------------------------------------------------------------
const typographyBySize: Record<AvatarSize, TypographyVariant> = {
  sm: 'label-sm',
  md: 'body-md',
  lg: 'body-lg',
}

export function Avatar({
  user,
  size = 'md',
  onClick,
  showRing = false,
  className,
}: AvatarProps) {
  const initials = (user.initials || deriveInitials(user.name)).slice(0, 2)
  const color = pickColor(initials || user.name)

  // Track image load failure so we can fall back to initials. The `lastUrl`
  // shadow state lets us reset `imgFailed` during render whenever the
  // `avatarUrl` prop changes — the React-recommended "store information
  // from previous renders" pattern (avoids a cascading-render useEffect).
  const [imgFailed, setImgFailed] = useState(false)
  const [lastUrl, setLastUrl] = useState(user.avatarUrl)
  if (user.avatarUrl !== lastUrl) {
    setLastUrl(user.avatarUrl)
    setImgFailed(false)
  }

  const showImage = Boolean(user.avatarUrl) && !imgFailed

  // Always apply a palette colour — never fall back to a neutral grey
  // background. When `showImage` is true the image fills the circle so
  // the colour only briefly flashes during load; when initials are shown
  // the colour pair (light tint bg + 700-shade text) is the entire look.
  const containerClasses = [
    styles.root,
    styles[`size-${size}`],
    styles[`color-${color}`],
    showRing && styles.ring,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  // Inner content — image or initials. Initials use the project Typography
  // atom so size + weight + colour all flow through design-system tokens.
  const inner = showImage ? (
    <img
      className={styles.image}
      src={user.avatarUrl}
      alt=""
      loading="lazy"
      decoding="async"
      onError={() => setImgFailed(true)}
    />
  ) : (
    <Typography
      variant={typographyBySize[size]}
      weight="semibold"
      color="inherit"
      as="span"
      className={styles.initials}
    >
      {initials || '·'}
    </Typography>
  )

  // Clickable: render as a real <button> so tap targets are correct,
  // keyboard focus works, and `aria-label` describes the action.
  if (onClick) {
    return (
      <button
        type="button"
        className={containerClasses}
        onClick={() => onClick(user)}
        aria-label={`Open profile of ${user.name}`}
      >
        {inner}
      </button>
    )
  }

  // Non-clickable: a labelled image so screen readers announce who it is.
  return (
    <span
      className={containerClasses}
      role="img"
      aria-label={`Avatar of ${user.name}`}
    >
      {inner}
    </span>
  )
}
